import {
  HeadingGroup,
  ModalLayout,
  Pagination,
  SearchFilters,
  Table,
} from "@wac-ui-dashboard/wac_component_library";
import React from "react";
import { FaSort } from "react-icons/fa";
import useNotificationDeliveryReport from "./useNotificationDeliveryReport";
import EmptyData from "../../../Global/EmptyData";
import OrderColumn from "../../../Global/OrderColumn";
import Style from "./notificationDeliveryReport.module.scss";

const NotificationDeliveryReport = () => {
  const {
    notification,
    delivery,
    deliveryList,
    deliveryState,
    activeFilter,
    statusFilters,
    isLoading,
    isFetching,
    isSummaryLoading,
    showEditModal,
    paginationOptions,
    updateDeliveryHead,
    refetch,
    getRow,
    handleSearch,
    handleClearClick,
    handleFilterChange,
    handleSort,
    handlePagination,
    handlePageSize,
    handleEditColumnsClick,
    handleExport,
    handleBack,
  } = useNotificationDeliveryReport();

  const rows = deliveryList?.data?.result ?? [];

  return (
    <>
      <HeadingGroup
        title="Delivery Report"
        className="pro-mb-4"
        buttonTitle="Export CSV"
        handleClick={handleExport}
        extraClassName="pro-justify-between"
      />

      <div className="col-auto pro-pt-3 pro-pb-6">
        <button
          className="pro-btn pro-btn-link pro-p-0 pro-mb-3"
          onClick={handleBack}
        >
          <span className="material-symbols-outlined">arrow_back</span>
          <span>Back to Custom Notification</span>
        </button>

        {!isSummaryLoading && notification && (
          <div className={`${Style.header_card} pro-mb-4`}>
            <h5 className="pro-ttl pro-mb-1">{notification?.title}</h5>
            <p className={Style.message}>{notification?.message}</p>
            <div className={Style.meta}>
              <span>
                Type: <b>{notification?.notification_type}</b>
              </span>
              <span>
                Scheduled:{" "}
                <b>
                  {notification?.schedule_date} {notification?.schedule_time}
                </b>
              </span>
              <span>
                Filters: <b>{notification?.filters?.join(", ") || "—"}</b>
              </span>
              <span>
                Schedule status: <b>{notification?.schedule_status}</b>
              </span>
              {/* Where tapping it takes the user. Naming the target too, since
                  "Product" alone does not say which product. */}
              <span>
                Redirects to:{" "}
                <b>
                  {notification?.redirect_to
                    ? notification.redirect_target
                      ? `${notification.redirect_to} — ${notification.redirect_target}`
                      : notification.redirect_to
                    : "No redirection"}
                </b>
              </span>
              {notification?.launch_url && (
                <span>
                  Launch URL: <b>{notification.launch_url}</b>
                </span>
              )}
            </div>
          </div>
        )}

        {delivery && (
          <div className={`${Style.summary_strip} pro-mb-4`}>
            <Stat value={delivery.targeted} label="Targeted" />
            <Stat value={delivery.sent} label="Sent" />
            <Stat value={delivery.rejected} label="Rejected" />
            <Stat value={delivery.no_player_id} label="No device" />
            <Stat value={delivery.clicked} label="Clicked" />
            <Stat value={delivery.failed} label="Failed" />
            <Stat
              value={delivery.onesignal?.successful}
              label="OneSignal accepted"
              external
            />
            <Stat
              value={delivery.onesignal?.converted}
              label="OneSignal clicks"
              external
            />
          </div>
        )}

        {delivery?.recipients_truncated && (
          <div className={`${Style.notice} pro-mb-4`}>
            This send was larger than the per-recipient logging limit, so only
            users who were not reached are listed below. The counts above are
            complete.
          </div>
        )}

        {delivery?.dispatch_errors?.length > 0 && (
          <div className={`${Style.notice} pro-mb-4`}>
            OneSignal reported an error on this send:{" "}
            {delivery.dispatch_errors.join("; ")}
          </div>
        )}

        <div className={`${Style.filter_chips} pro-mb-4`}>
          {statusFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`${Style.chip} ${
                activeFilter.key === filter.key ? Style.active : ""
              }`}
              onClick={() => handleFilterChange(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <SearchFilters
          onSearchInput={handleSearch}
          loading={isLoading}
          SearchIcon={
            <span className="material-symbols-outlined"> search </span>
          }
          showClearFilters={deliveryState?.search !== ""}
          handleClear={handleClearClick}
          searchInputProps={{ value: deliveryState?.search }}
          handleActionClick={handleEditColumnsClick}
        />

        {rows.length === 0 ? (
          <>
            {/* EmptyData renders a fixed "No Data Found", which cannot tell
                "not sent yet" from "sent, but nothing matches this filter" —
                say which above it. */}
            <div className={`${Style.notice} pro-mt-3`}>
              {delivery?.has_sent
                ? "No recipients match this filter. Try “All”."
                : "This notification has not been sent yet, so there is nothing to report."}
            </div>
            <EmptyData />
          </>
        ) : (
          <Table
            multiSelect={false}
            data={rows}
            uniqueID={"id"}
            fields={deliveryList?.data?.fields || []}
            SortIcon={<FaSort />}
            handleSort={handleSort}
            getRow={getRow}
            loading={isFetching}
            perpage={deliveryState?.currentPageSize}
            assignable={false}
            deletable={false}
            editable={false}
            showCheckBox={false}
          />
        )}

        {rows.length > 0 && (
          <Pagination
            currentPage={deliveryState?.currentPage}
            totalPageCount={Math.ceil(
              deliveryList?.data?.total_count / deliveryState.currentPageSize
            )}
            defaultValue={paginationOptions?.filter(
              (item) => item?.value === deliveryState?.currentPageSize
            )}
            onPageChange={handlePagination}
            options={paginationOptions}
            onActionChange={handlePageSize}
            center
          />
        )}

        <ModalLayout
          show={showEditModal}
          handleClose={handleEditColumnsClick}
          backdrop="static"
        >
          <div className="pro-m-5">
            <OrderColumn
              title={"Choose which columns you see"}
              refetchData={refetch}
              tableFields={deliveryList?.data?.fields || []}
              moduleId={deliveryList?.data?.module_id}
              updateData={updateDeliveryHead}
            />
          </div>
        </ModalLayout>
      </div>
    </>
  );
};

const Stat = ({ value, label, external }) => (
  <div className={`${Style.stat} ${external ? Style.external : ""}`}>
    <div className={Style.value}>{value ?? "—"}</div>
    <div className={Style.label}>{label}</div>
  </div>
);

export default NotificationDeliveryReport;
